// Login session state, persisted to localStorage so a refresh keeps the
// user signed in until they hit logout (or the optional 30-day "remember
// me" expiry kicks in).
//
// Phase 1 placeholder: this is purely client-side. There's no server
// session; every API call still goes through DevActorAuth on the
// backend with the actor preset stored here. When Microsoft Entra OIDC
// lands, the same store gets repurposed to hold the issued JWT + its
// claims, and the api wrapper's auth header swaps from
// X-Sanshi-Dev-Actor to Authorization: Bearer <jwt>.

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { actorRole, useActor } from './actor-store.ts';
import type { DevActorPreset } from './api.ts';

interface SessionParams {
  email: string;
  name: string;
  /** V151 (2026-06-14) — optional 中文名 for "Samson (姚舜晏)" UI display. */
  nameZh?: string | null;
  actor: DevActorPreset;
  /** When set, the session expires N days from now. Null = current tab only. */
  rememberDays: number | null;
}

interface AuthState {
  email: string | null;
  name: string | null;
  nameZh: string | null;
  /** Cached so the rest of the SPA can drive role-based gating without
   *  recomputing from actor on every render. */
  actor: DevActorPreset | null;
  /** ISO-8601 expiry. When null the session has no explicit expiry. */
  expiresAt: string | null;
  /** Backend-issued session JWT (from magic-link login). When present, api.ts
   *  sends Authorization: Bearer <localToken>. Null when using Entra or dev. */
  localToken: string | null;
  setSession: (s: SessionParams) => void;
  setLocalToken: (token: string | null) => void;
  clearSession: () => void;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      email: null,
      name: null,
      nameZh: null,
      actor: null,
      expiresAt: null,
      localToken: null,
      setSession: (s) => {
        const expiresAt = s.rememberDays
          ? new Date(Date.now() + s.rememberDays * 24 * 60 * 60 * 1000).toISOString()
          : null;
        set({
          email: s.email,
          name: s.name,
          nameZh: s.nameZh ?? null,
          actor: s.actor,
          expiresAt,
        });
        useActor.getState().setActor(s.actor);
      },
      setLocalToken: (token) => set({ localToken: token }),
      clearSession: () => {
        set({
          email: null,
          name: null,
          nameZh: null,
          actor: null,
          expiresAt: null,
          localToken: null,
        });
      },
    }),
    {
      name: 'sanshi-auth-session',
      version: 2,
    },
  ),
);

/** Read the current localToken without a re-render subscription. api.ts calls
 *  this on every request to attach Authorization header. */
export function getLocalToken(): string | null {
  return useAuth.getState().localToken;
}

/** True if there's a logged-in session and it hasn't expired. */
export function isSessionValid(state: AuthState): boolean {
  if (!state.email) return false;
  if (state.expiresAt && new Date(state.expiresAt).getTime() < Date.now()) {
    return false;
  }
  return true;
}

/** Convenience hook — returns true when the SPA should render the app
 *  shell (rather than redirect to /login). */
export function useIsSessionValid(): boolean {
  return useAuth(isSessionValid);
}

/** Convenience — derive the role bucket from the cached actor. Useful
 *  for admin-only UI gating on screens that previously called useIsAdmin
 *  directly off the dev actor. */
export function useSessionRole(): 'admin' | 'member' | 'lp' | null {
  return useAuth((s) => (s.actor ? actorRole(s.actor) : null));
}
